import { StatusBadge } from "@/components/shared/finance/StatusBadge";
import {
  createPeekContent,
  DocumentSummaryContent,
  type LineItemRow,
} from "@/components/shared/relationships/peek-content";
import { registerPeekType } from "@/components/shared/relationships/peek-registry";
import { usePurchaseOrderDetail } from "@/features/purchase-orders/hooks/use-purchase-orders-data";
import { getProductDisplayName } from "@/lib/product-display";
import { formatDocumentUid } from "@/lib/format";

function PurchaseOrderContent({
  detail,
}: {
  detail: NonNullable<ReturnType<typeof usePurchaseOrderDetail>["data"]>;
}) {
  const po = detail.purchase_order;
  const lines: LineItemRow[] = (detail.products ?? []).map((product) => ({
    id: product.id,
    productId: product.id,
    productLabel: getProductDisplayName(product, ""),
    quantity: product.quantity,
    total: product.cost,
  }));

  return (
    <DocumentSummaryContent
      data={{
        accountId: po.account_id,
        accountLabel: "Vendor",
        accountName: po.gl_accounts?.name,
        badge: <StatusBadge status={po.payment_status} />,
        balance: po.balance,
        date: po.date,
        lineItemsEmptyMessage: "No products found",
        lines,
        linesLabel: "Products",
        title: formatDocumentUid(po.uid, po.id),
        totalAmount: po.total_amount,
        totalPaid: po.total_paid,
      }}
    />
  );
}

/** Purchase-order quick-view content, registered on import via `src/app/peek-registrations.ts`. */
export const PurchaseOrderPeekContent = createPeekContent({
  emptyMessage: "No purchase order found",
  render: (detail) => <PurchaseOrderContent detail={detail} />,
  useDetail: usePurchaseOrderDetail,
});

registerPeekType("purchase-order", PurchaseOrderPeekContent);
